import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRememberFilters } from '@/hooks/useRememberFilters';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';

import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useAppState } from '@/app/appContext';
import { useOpenZone } from '@/hooks/useOpenZone';
import { AllBranchesReport } from '@/components/portfolio/AllBranchesReport';
import { useTelegramAutoLogin } from '@/hooks/useTelegramAutoLogin';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle, usePageHeaderSlots } from '@/contexts/PageHeaderContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { Button } from '@/components/ui/button';
import { MobileSubHeader } from '@/components/layout/MobileSubHeader';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/components/ui/date-picker';
import { Tooltip } from '@/components/ui/tooltip';
import { ZoomSegment, type Zoom } from '@/components/report/ZoomSegment';
import { DailyReportView } from '@/components/report/DailyReportView';
import { WeekReport } from '@/components/report/WeekReport';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

// ── date helpers (one picker drives BOTH День and Неделя, in the header) ──────

export function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function todayIso(): string {
  return isoOf(new Date());
}
export function shiftIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return isoOf(date);
}
export function fmtShort(iso: string, locale: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
// Monday of the most recent COMPLETED week (the latest week a report covers).
function lastCompletedMonday(): string {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const sinceMon = t.getDay() === 0 ? 6 : t.getDay() - 1;
  t.setDate(t.getDate() - sinceMon - 7);
  return isoOf(t);
}
// Monday of the week containing the given date (the week picker accepts any
// day and snaps to its week).
function mondayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const sinceMon = dt.getDay() === 0 ? 6 : dt.getDay() - 1;
  dt.setDate(dt.getDate() - sinceMon);
  return isoOf(dt);
}
function fmtRange(monIso: string, locale: string): string {
  const mon = (() => { const [y, m, d] = monIso.split('-').map(Number); return new Date(y, m - 1, d); })();
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const fmt = (x: Date) => x.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  return `${fmt(mon)} — ${fmt(sun)}`;
}

/** Заглавная только первая буква: CSS capitalize делал «16 Сент.». */
const capFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Короткая неделя для мобильной полосы: «8–14 сент.», через месяц — «29 сент. – 5 окт.». */
function fmtRangeShort(monIso: string, locale: string): string {
  const [y, m, d] = monIso.split('-').map(Number);
  const mon = new Date(y, m - 1, d);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const monthOf = (x: Date) => x.toLocaleDateString(locale, { month: 'short' });
  if (mon.getMonth() === sun.getMonth()) return `${mon.getDate()}–${sun.getDate()} ${monthOf(sun)}`;
  return `${mon.getDate()} ${monthOf(mon)} – ${sun.getDate()} ${monthOf(sun)}`;
}

// One stepper shape for both zooms: ‹ label › with outline chevrons. Daily steps
// by a day, weekly by a week — same component, same place (header + mobile bar).
// When `pickerValue`/`onPickDate` are supplied the label itself becomes a
// clickable date-picker trigger (styled flat to keep the ‹ date › look), so the
// operator can jump to any day directly instead of only stepping.
export function DateStepper({
  label, onPrev, onNext, nextDisabled, pickerValue, onPickDate, pickerMax, locale, weekPicker, compact = false,
}: {
  label: string;
  onPrev: () => void;
  onNext: () => void;
  nextDisabled: boolean;
  pickerValue?: string;
  onPickDate?: (iso: string) => void;
  pickerMax?: string;
  locale?: string;
  /** Week zoom: the calendar highlights/selects whole Mon–Sun weeks. */
  weekPicker?: boolean;
  /** Мобильная полоса второго уровня: стрелки без рамки, подпись ужимается. */
  compact?: boolean;
}) {
  const { t } = useT();
  const arrow = compact ? 'size-8 shrink-0 border-0 shadow-none' : 'size-8 shrink-0';
  return (
    <div className={cn('flex items-center gap-1', compact && 'min-w-0 gap-0')}>
      <Button variant={compact ? 'ghost' : 'outline'} size="icon" className={arrow} onClick={onPrev} aria-label={t('summaries.prevDay')}>
        <ChevronLeft className="size-4" />
      </Button>
      {pickerValue && onPickDate ? (
        <DatePicker
          value={pickerValue}
          onChange={(next) => { if (!pickerMax || next <= pickerMax) onPickDate(next); }}
          locale={locale}
          weekMode={weekPicker}
          formatLabel={() => capFirst(label)}
          ariaLabel={label}
          className={compact ? 'min-w-0' : 'min-w-24 sm:min-w-32'}
          triggerClassName="h-8 w-full justify-center gap-1 border-0 bg-transparent px-2 shadow-none hover:bg-muted focus-visible:ring-1 sm:gap-2 sm:px-3"
        />
      ) : (
        <span className="min-w-24 whitespace-nowrap text-center text-sm font-medium sm:min-w-32">{capFirst(label)}</span>
      )}
      <Button variant={compact ? 'ghost' : 'outline'} size="icon" className={arrow} onClick={onNext} disabled={nextDisabled} aria-label={t('summaries.nextDay')}>
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
}

/**
 * The merged "Отчёт" surface: Итоги дня + Итоги недели behind a День/Неделя
 * picker. The page owns BOTH the day and the week cursor and renders ONE shared
 * date stepper for whichever zoom is active — desktop in the top bar next to the
 * branch picker, mobile in a compact sub-header. Day/Week views are pure
 * presenters fed the chosen date (no date state of their own).
 */
export default function OverviewReportPage() {
  useTelegramAutoLogin();
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  usePageTitle(t('report.title'), t('report.subtitle'));

  const { zoneId } = useCurrentZone();
  const { allBranches } = useAppState().zone;
  const openZone = useOpenZone();
  const [params, setParams] = useSearchParams();
  useRememberFilters(['date', 'z']);
  const zoom: Zoom = params.get('z') === 'week' ? 'week' : 'day';

  // Reports cover the past — day defaults to yesterday, week to the last
  // completed week. A `date` URL param (the TG report's «Смотреть полный
  // отчёт» link carries one) opens that specific report instead.
  const [day, setDay] = useState<string>(() => {
    const d = params.get('date');
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) && d <= todayIso()
      ? d
      : shiftIso(todayIso(), -1);
  });
  const [monday, setMonday] = useState<string>(() => {
    const d = params.get('date');
    const mon = d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? mondayOf(d) : lastCompletedMonday();
    return mon <= lastCompletedMonday() ? mon : lastCompletedMonday();
  });
  // Листают стрелками — запрос за последним днём/неделей, а не за каждым промежуточным (#3271).
  const dayQ = useDebouncedValue(day, 350);
  const mondayQ = useDebouncedValue(monday, 350);

  // Keep `date` in the URL so the current report is shareable/linkable.
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set('date', zoom === 'week' ? monday : day);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // params identity churns with every set — keyed on the real inputs.
  }, [day, monday, zoom]);

  const setZoom = (z: Zoom) => {
    const next = new URLSearchParams(params);
    next.set('z', z);
    setParams(next, { replace: true });
  };

  // The one control, parameterised by zoom. The week label is a picker too:
  // pick any day → jump to that day's week (capped at the last completed one).
  const stepper = (compact: boolean) => zoom === 'week'
    ? <DateStepper
        compact={compact}
        label={compact ? fmtRangeShort(monday, locale) : fmtRange(monday, locale)}
        onPrev={() => setMonday((w) => shiftIso(w, -7))}
        onNext={() => setMonday((w) => shiftIso(w, 7))}
        nextDisabled={monday === lastCompletedMonday()}
        pickerValue={monday}
        onPickDate={(iso) => {
          const mon = mondayOf(iso);
          setMonday(mon <= lastCompletedMonday() ? mon : lastCompletedMonday());
        }}
        locale={locale}
        weekPicker
      />
    : <DateStepper
        compact={compact}
        label={fmtShort(day, locale)}
        onPrev={() => setDay((d) => shiftIso(d, -1))}
        onNext={() => setDay((d) => shiftIso(d, 1))}
        nextDisabled={day === todayIso()}
        pickerValue={day}
        onPickDate={setDay}
        pickerMax={todayIso()}
        locale={locale}
      />;

  const infoButton = (
    <Tooltip content={`${t('report.title')} — ${t('report.subtitle')}`} side="bottom">
      <button
        type="button"
        className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={t('report.subtitle')}
      >
        <Info className="size-4" />
      </button>
    </Tooltip>
  );

  const controls = (
    <div className="flex items-center gap-3">
      <ZoomSegment value={zoom} onChange={setZoom} />
      {stepper(false)}
    </div>
  );
  // Телефон: тонкая полоса второго уровня, всё помещается без прокрутки (AI12.7).
  const mobileBar = (
    <MobileSubHeader className="justify-between">
      <ZoomSegment value={zoom} onChange={setZoom} compact />
      {stepper(true)}
    </MobileSubHeader>
  );

  // Desktop: switcher + date stepper centered in the top bar; (i) on the right.
  usePageHeaderSlots(
    { center: controls, actions: infoButton },
    [zoom, day, monday, lang],
  );

  // «Все филиалы»: portfolio cards instead of the single-branch report; the
  // День/Неделя switcher + date stepper stay (they parameterise the drill-down
  // and, later, the per-branch mini report cards). Clicking a card opens that
  // branch's full report at the currently chosen zoom + date.
  if (allBranches) {
    return (
      <div className="flex flex-col">
        {mobileBar}
        <AllBranchesReport
          zoom={zoom}
          date={zoom === 'week' ? monday : day}
          onOpenZone={(id) => openZone(id, '/usage/report', { z: zoom, date: zoom === 'week' ? monday : day })}
        />
      </div>
    );
  }

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    <div className="flex flex-col">
      {/* Mobile sub-header: the same switcher + stepper (desktop uses the top
          bar). Sticky to the top of the scroll container (<main> already sits
          below the Header). */}
      {mobileBar}

      {/* Full-bleed on desktop; single centered column on phones. */}
      <div className="m-bleed mx-auto w-full max-w-3xl p-4 md:p-6 xl:max-w-none xl:px-8">
        {zoom === 'week'
          ? <WeekReport zoneId={zoneId} monday={mondayQ} />
          : <DailyReportView zoneId={zoneId} date={dayQ} pending={dayQ !== day} />}
      </div>
    </div>
  );
}
